import { Button } from '../button/button';
import { Flex } from '../flex/flex';
import { palette } from '@instamenu/ui-vanilla-extract';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { Stack } from '../stack/stack';
import { Text } from '../text/text';

export type SegmentedOption = {
  label: React.ReactNode;
  description?: React.ReactNode;
  value: string;
};

export const SegmentedControls = ({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  /**
   * Accessible name for the radiogroup. Required — screen readers announce
   * a `role="radiogroup"` with no name as generic "radio group", so callers
   * must pass a label that describes what's being chosen (typically the
   * same visible label rendered above the control).
   */
  ariaLabel: string;
}) => {
  // When *any* option has a description (e.g. storefront's order-schedule
  // where every option carries one), each pill renders two lines and reads
  // naturally top-left aligned. When *no* option has a description (e.g.
  // dashboard's export format toggle), the single label should be centered —
  // otherwise it hugs the left edge and looks broken. The `some` check
  // matches the "any" case rather than requiring "every" option to opt in.
  const anyDescription = options.some((option) => option.description);
  const alignItems = anyDescription ? 'flex-start' : 'center';
  const justifyContent = anyDescription ? 'flex-start' : 'center';

  return (
    <Flex role="radiogroup" aria-label={ariaLabel} css={{ gap: themeVars.pad[12] }}>
      {options.map((option) => (
        <Button
          key={option.value}
          variant="text"
          role="radio"
          aria-checked={value === option.value}
          css={{
            border: `1px solid ${value === option.value ? themeVars.color.brand : palette.gray50}`,
            borderRadius: themeVars.radii.input,
            padding: '10px 16px',
            alignItems,
            justifyContent,
          }}
          onClick={() => {
            onChange(option.value);
          }}
        >
          <Stack css={{ gap: 4, alignItems: anyDescription ? 'flex-start' : 'center' }}>
            <Text variant="caption1" css={{ fontWeight: 400 }}>
              {option.label}
            </Text>
            {option.description && (
              <Text variant="caption2" css={{ color: palette.gray600, fontWeight: 400 }}>
                {option.description}
              </Text>
            )}
          </Stack>
        </Button>
      ))}
    </Flex>
  );
};
